import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { Tooltip } from '@/shared/ui/tooltip/Tooltip';

const buttonVariants = cva('', {
  variants: {
    variant: {
      primary: 'bg-brand-600 text-white hover:bg-brand-500',
      outline: 'border border-stone-200 bg-white hover:bg-stone-50 hover:text-stone-900',
      ghost: 'hover:bg-stone-50 hover:text-stone-900',
      // ダーク背景上で使う ghost。フローティングアクションバー等で使用する。
      'ghost-on-dark': 'text-text-inverse hover:bg-white/10 hover:text-text-inverse',
      // ダーク背景上の destructive 動線（破壊的アクション）。red-400 系で映える色。
      'destructive-on-dark': 'text-red-400 hover:bg-white/10 hover:text-red-300',
      destructive: 'bg-red-600 text-white hover:bg-red-500',
      link: 'text-brand-800 underline underline-offset-4 hover:text-brand-700',
      'non-outline':
        'outline-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0',
      /**
       * グラデーションボーダースタイル
       * Figma: https://www.figma.com/design/K9BryoBh0t69xPihm3VBho/DnR_DesignSystem?node-id=5463-7137
       * background-clip 手法でグラデーションボーダーを実現。
       */
      'ai-gradient': [
        'border border-transparent',
        'bg-transparent',
        '[background-image:linear-gradient(var(--color-surface-base),var(--color-surface-base)),var(--color-border-ai-gradient-1)]',
        '[background-origin:padding-box,border-box]',
        '[background-clip:padding-box,border-box]',
        'shadow-sm',
        '[color:var(--color-text-accent)]',
        '[&:hover:not(:disabled)]:[background-image:linear-gradient(var(--color-component-button-gradation-hover),var(--color-component-button-gradation-hover)),var(--color-border-ai-gradient-1)]',
        '[&:hover:not(:disabled)]:[color:var(--color-text-accent)]',
      ].join(' '),
    },
    size: {
      regular: 'h-9 px-4 py-[7.5px]',
      sm: 'h-8 px-3 py-1.5',
      mini: 'h-[26px] px-3 py-1',
      lg: 'h-11 rounded-md px-8',
      icon: 'h-8 w-8 py-1.5',
      none: 'h-auto',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'sm',
  },
});

export type ButtonBaseProps = ComponentProps<'button'> & VariantProps<typeof buttonVariants>;

/**
 * ボタンに内蔵するツールチップ設定。
 * 呼び出し側で `<Tooltip>` ラップを書かずに `tooltip` prop だけでツールチップを付けられる。
 *
 * disabled なボタンでもツールチップを表示できる: disabled な `<button>` は pointer-events が
 * 発火しない（`disabled:pointer-events-none`）が、disabled 時は内部で `<span>` ラップして
 * span でホバーを拾うため、ホバーでツールチップが表示される。
 *
 * 「disabled 理由を出す」「disabled 時は出さない」の両方を `enabled` で制御できる:
 * - disabled でも出す → `enabled` 省略（デフォルト true）
 * - disabled のときは出さない → `enabled: !disabled` のように条件を渡す
 */
type ButtonTooltip = {
  content: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  contentClassName?: string;
  /** ツールチップの表示可否。デフォルト true。false のときラッパーごと消える（DOM 追加なし） */
  enabled?: boolean;
};

// asChild=true 時は Slot が単一子要素しか受け取れないため、自前で spinner を挿入する isLoading
// との同時使用を型で禁止する。asChild の場合はローディング表示を呼び出し側で children として渡す。
type ButtonProps = ButtonBaseProps &
  ({ asChild?: false; isLoading?: boolean } | { asChild: true; isLoading?: never }) & {
    tooltip?: ButtonTooltip;
  };

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  isLoading = false,
  disabled,
  children,
  tooltip,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : 'button';
  const buttonElement = (
    <Comp
      className={cn(
        // レイアウト・配置
        'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md',
        // カラー/トランジション
        'ring-offset-background transition-colors',
        // フォーカス/アクセシビリティ
        'focus:ring-2 focus:ring-focus-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
        // 状態
        'cursor-pointer disabled:pointer-events-none disabled:opacity-50',
        // SVGアイコン用
        '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
        // text
        'font-medium text-sm',
        buttonVariants({ variant, size, className }),
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {/*
        Slot (asChild) は単一の React 要素のみを受け取るため、isLoading の spinner との
        Fragment は asChild=false の場合だけ展開する。asChild 時はローディング表示は
        呼び出し側で children として渡すこと。
      */}
      {asChild ? (
        children
      ) : (
        <>
          {isLoading && <ArrowPathIcon className="animate-spin" />}
          {children}
        </>
      )}
    </Comp>
  );

  if (tooltip) {
    // disabled なボタンは pointer-events が無効でホバーが届かないため、span でラップして
    // span 側でホバーを拾い、disabled 時でもツールチップを表示できるようにする。
    const isDisabled = disabled || isLoading;
    const trigger = isDisabled ? (
      <span className="inline-flex">{buttonElement}</span>
    ) : (
      buttonElement
    );
    return (
      <Tooltip
        tooltipContent={tooltip.content}
        side={tooltip.side}
        sideOffset={tooltip.sideOffset}
        contentClassName={tooltip.contentClassName}
        enabled={tooltip.enabled ?? true}
      >
        {trigger}
      </Tooltip>
    );
  }

  return buttonElement;
};

Button.displayName = 'Button';

export { Button };
